import type { NavKey } from "../components/dashboard";

import { formatDateTime, formatRelativeTime } from "./time";

interface DashboardHeaderInput {
  activeView: NavKey;
  generatedAt: number;
}

interface DashboardHeaderCopy {
  kicker: string;
  title: string;
  subtitle: string;
}

export function getDashboardHeaderCopy({
  activeView,
  generatedAt,
}: DashboardHeaderInput): DashboardHeaderCopy {
  const kickerMap: Partial<Record<NavKey, string>> = {
    admin: "Platform Admin",
    "agents-instances": "Agent Blackboard",
    "agents-templates": "Agent Templates",
    integrations: "Integrations",
    overview: "Welcome back",
    plans: "Plan Management",
    "release-notes": "What's New",
    "repo-chat": "Repository Intelligence",
    settings: "Configuration",
    usage: "Statistics",
    wiki: "Knowledge Base",
  };

  const titleMap: Partial<Record<NavKey, string>> = {
    admin: "Admin Dashboard",
    "agents-instances": "Agents — Instances",
    "agents-templates": "Agents — Templates",
    integrations: "Integrations",
    overview: "Arti Overview",
    plans: "Plan Snapshots",
    "release-notes": "Release Notes",
    "repo-chat": "Repository Chat",
    settings: "Settings",
    usage: "Statistics",
    wiki: "Know-How",
  };

  return {
    kicker: kickerMap[activeView] ?? "Welcome back",
    subtitle: resolveHeaderSubtitle(activeView, generatedAt),
    title: titleMap[activeView] ?? "Arti Dashboard",
  };
}

function resolveHeaderSubtitle(
  activeView: NavKey,
  generatedAt: number,
): string {
  switch (activeView) {
    case "overview": {
      return `Updated ${formatRelativeTime(generatedAt)} · ${formatDateTime(generatedAt)}`;
    }
    case "usage": {
      return "Review usage, cost, and timing performance across providers, stages, and runs.";
    }
    case "plans": {
      return "Browse plan snapshots and revision history.";
    }
    case "integrations": {
      return "Manage issue trackers, source control, and repository indexing.";
    }
    case "agents-instances": {
      return "Spawn, monitor, and interact with persistent agent instances.";
    }
    case "agents-templates": {
      return "Reusable agent configurations — define defaults for spawn.";
    }
    case "repo-chat": {
      return "Open a dedicated cross-repository workspace for grounded answers, debugging, and code discovery.";
    }
    case "settings": {
      return "Configure agent budgets and inspect schema telemetry.";
    }
    case "admin": {
      return "Manage organizations, monitor nodes, and view global queue status.";
    }
    case "wiki": {
      return "Practical guides and know-how for getting the most out of Arti.";
    }
    case "release-notes": {
      return "Explore new features, improvements, and fixes in each release.";
    }
    default: {
      return "";
    }
  }
}
